import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { makeStyles, Fab, Zoom } from '@material-ui/core'
import { AttachmentProvider } from '../contexts/AttachmentContext'
import { AttachmentViewer } from '../Attachments/AttachmentViewer'
import Icon from '../../../../components/atoms/Icon'
import { useAIThread, useInvokeDenaliAssistant, useRetryMessage } from '../../../../api/denaliAssistant'
import MessageInput from '../common/MessageInput'
import Background from '../../../../providers/BackgroundProvider/Background'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import DenaliLoader from '../common/DenaliLoader'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import MessageList from './MessageList'
import { useScrollBehavior, ScrollEvent } from './useScrollBehavior'

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row', // Changed to row for side-by-side layout
    overflow: 'hidden' // Prevent the container itself from scrolling
  },

  chatWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0, // Allow flex item to shrink below its content size
    position: 'relative' // For positioning the scroll button
  },

  messagesWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    overflowY: 'auto',
    scrollBehavior: 'smooth',
    position: 'relative',
    width: '100%'
  },
  messageContent: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    padding: 'var(--padding-md)'
  },
  inputSection: {
    flexShrink: 0,
    padding: 'var(--padding-md)',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: '80px',
    right: '20px',
    backgroundColor: 'var(--surface-bg-color)',
    color: 'var(--color-primary-500)',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d1)'
    }
  }
}))

function ChatInterface () {
  const classes = useStyles()
  const { threadId } = useParams()
  const history = useHistory()
  const messagesEndRef = useRef(null)
  const messagesWrapperRef = useRef(null)
  const messageInputRef = useRef(null)
  const [isAtBottom, setIsAtBottom] = useState(true)

  // Track previous generating message ID to detect start/finish transitions
  const prevGeneratingMessageIdRef = useRef(null)

  const isNewThread = threadId === 'new'

  // Fetch thread data
  const { data: threadData, isLoading, isSuccess } = useAIThread(threadId, {
    enabled: !isNewThread
  })

  // Mutations
  const invokeMutation = useInvokeDenaliAssistant()
  const retryMutation = useRetryMessage()

  // Get messages from thread data
  const messages = threadData?.thread?.messages || []

  // Get focus from thread metadata context
  const threadFocus = threadData?.thread?.metadata?.context?.focus || null

  // Find the generating message (if any)
  const generatingMessage = messages.find(m => m.isGenerating)
  const generatingMessageId = generatingMessage?.threadMessageId || null

  // Check if AI is currently processing/generating
  const isProcessing = invokeMutation.isLoading || !!generatingMessage

  // Initialize scroll behavior manager
  const {
    handleEvent,
    handleUserScroll,
    resetAttention,
    checkIsAtBottom
  } = useScrollBehavior(messagesWrapperRef)

  // Check if user is at bottom of scroll
  const checkIfAtBottom = useCallback(() => {
    const isBottom = checkIsAtBottom(50)
    setIsAtBottom(isBottom)
  }, [checkIsAtBottom])

  // Scroll to bottom function (for the FAB button)
  const scrollToBottom = useCallback(() => {
    handleEvent(ScrollEvent.USER_SUBMITTED) // Reuse the scroll-to-bottom action
    setIsAtBottom(true)
  }, [handleEvent])

  // Monitor scroll position and handle user scroll during generation
  useEffect(() => {
    const wrapper = messagesWrapperRef.current
    if (!wrapper) return

    const onScroll = () => {
      checkIfAtBottom()
      handleUserScroll(isProcessing)
    }

    wrapper.addEventListener('scroll', onScroll)
    return () => wrapper.removeEventListener('scroll', onScroll)
  }, [checkIfAtBottom, handleUserScroll, isProcessing])

  // Handle conversation loaded event
  useEffect(() => {
    if (!isNewThread && isSuccess && messages.length > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        handleEvent(ScrollEvent.CONVERSATION_LOADED)
      }, 100)
    }
  }, [isNewThread, isSuccess, messages.length, handleEvent])

  // Detect generation start/finish transitions
  useEffect(() => {
    const prevId = prevGeneratingMessageIdRef.current
    const currentId = generatingMessageId

    // Generation started: no previous ID, now we have one
    if (!prevId && currentId) {
      resetAttention()
      handleEvent(ScrollEvent.GENERATION_STARTED)
    }

    // Generation finished: had an ID, now we don't
    if (prevId && !currentId) {
      handleEvent(ScrollEvent.GENERATION_FINISHED)
    }

    // Update the ref for next comparison
    prevGeneratingMessageIdRef.current = currentId
  }, [generatingMessageId, handleEvent, resetAttention])

  const handleSendMessage = useCallback(async (message, attachments = []) => {
    if (!message.trim()) return

    // Fire the user submitted event immediately
    handleEvent(ScrollEvent.USER_SUBMITTED)

    try {
      // Send the message - thread will be created automatically if needed
      const result = await invokeMutation.mutateAsync({
        threadId: isNewThread ? undefined : threadId,
        prompt: message,
        context: {
          currentView: 'ai-assistant-chat'
        },
        options: {
          modelStrategy: 'balanced',
          streamResponse: true,
          includeTrailOfThought: true
        },
        attachments
      })

      // Clear the input after successful send
      messageInputRef.current?.clear()

      // If this was a new thread, update URL to the new thread
      if (isNewThread && result.threadId) {
        history.replace(`/admin/denali/analysis/assistant/chat/${result.threadId}`)
      }

      setIsAtBottom(true)
    } catch (error) {
      console.error('Failed to send message:', error)
      // Clear sending state even on error
      messageInputRef.current?.clear()
      // TODO: Show error toast
    }
  }, [threadId, isNewThread, invokeMutation, history, handleEvent])

  const handleRetry = useCallback(async (message) => {
    if (!threadId || !message.threadMessageId) return

    // Fire the user submitted event (retry is similar to submit)
    handleEvent(ScrollEvent.USER_SUBMITTED)

    try {
      await retryMutation.mutateAsync({
        threadId,
        threadMessageId: message.threadMessageId
      })
      // The mutation will invalidate the query cache, which will refetch and show updated messages
      setIsAtBottom(true)
    } catch (error) {
      console.error('Failed to retry message:', error)
      // TODO: Show error toast
    }
  }, [threadId, retryMutation, handleEvent])

  const parentInfo = useMemo(() => {
    return {
      parentType: 'thread',
      parentId: threadId
    }
  }, [threadId])

  if (!isNewThread && isLoading) {
    return (
      <div className={classes.chatContainer}>
        <FlexColumn className='gap-md items-center justify-center w-full' style={{ padding: '40px' }}>
          <DenaliLoader />
          <FadeIn style={{ color: 'var(--dai-color-text-dim)' }} delay={1} duration={2}>Loading Conversation</FadeIn>
        </FlexColumn>
      </div>
    )
  }

  return (
    <Background
      light='var(--dai-bg)'
      dark='var(--dai-bg)'
    >
      <AttachmentProvider threadId={threadId} focus={threadFocus}>
        <div className={classes.chatContainer}>
          <div className={classes.chatWrapper}>
            <div ref={messagesWrapperRef} className={classes.messagesWrapper}>
              <div className={classes.messageContent}>
                <MessageList
                  parentInfo={parentInfo}
                  messages={messages}
                  isNewThread={isNewThread}
                  onRetry={handleRetry}
                  isRetrying={retryMutation.isLoading}
                  onSuggestionClick={(prompt) => {
                    // Set the suggestion in the input so user can review/modify before sending
                    messageInputRef.current?.setValue(prompt)
                  }}
                />
                <div ref={messagesEndRef} style={{ height: '1px' }} />
              </div>
            </div>
            {/* Scroll to bottom button */}
            <Zoom in={!isAtBottom}>
              <Fab
                className={classes.scrollToBottomButton}
                size='small'
                onClick={scrollToBottom}
                style={{ zIndex: '1000' }}
                aria-label='Scroll to bottom'
              >
                <Icon name='chevronDown' />
              </Fab>
            </Zoom>
            <div className={classes.inputSection}>
              <MessageInput
                ref={messageInputRef}
                onSend={handleSendMessage}
                disabled={isProcessing}
                placeholder={isNewThread ? 'Start a new conversation...' : 'Type a message...'}
                submissionMode='external'
              />
            </div>
          </div>
          <AttachmentViewer />
        </div>
      </AttachmentProvider>
    </Background>
  )
}

export default ChatInterface
